import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { 
  Save, Eye, ArrowLeft, Plus, Trash2, GripVertical, 
  Type, Heading, Image, Square, Minus, Code, Target,
  LayoutGrid, Box, AlignVerticalSpaceAround, PanelTop, Copy, PanelLeft
} from 'lucide-react';
import PageSidebarSettings from '../../components/PageSidebarSettings';

const COMPONENT_TYPES = [
  { type: 'heading', label: 'Heading', icon: Heading, category: 'content' },
  { type: 'text', label: 'Text Block', icon: Type, category: 'content' },
  { type: 'image', label: 'Image', icon: Image, category: 'content' },
  { type: 'button', label: 'Button', icon: Square, category: 'content' },
  { type: 'html', label: 'HTML Code', icon: Code, category: 'content' },
  { type: 'tool', label: 'Tool Plugin', icon: Target, category: 'content' },
  { type: 'divider', label: 'Divider', icon: Minus, category: 'layout' },
  { type: 'spacer', label: 'Spacer', icon: AlignVerticalSpaceAround, category: 'layout' },
  { type: 'section', label: 'Section', icon: PanelTop, category: 'layout' },
  { type: 'container', label: 'Container', icon: Box, category: 'layout' },
  { type: 'columns', label: 'Columns', icon: LayoutGrid, category: 'layout' },
];

function SortableComponent({ component, onUpdate, onDelete, onDuplicate, isActive }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: component.id });
  
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="mb-3">
      <Card className={`bg-slate-700 border-slate-600 ${isDragging ? 'ring-2 ring-blue-500 shadow-xl' : ''} ${isActive ? 'ring-2 ring-blue-400' : ''}`}>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <div 
              {...attributes} 
              {...listeners} 
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded cursor-grab active:cursor-grabbing touch-none"
            >
              <GripVertical className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <ComponentEditor component={component} onUpdate={onUpdate} />
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); onDuplicate(component); }} 
              className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-600 rounded"
              title="Duplicate Component"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button 
              onClick={(e) => { e.stopPropagation(); onDelete(component.id); }} 
              className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-600 rounded"
              title="Delete Component"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ComponentEditor({ component, onUpdate }) {
  const updateContent = (key, value) => {
    onUpdate(component.id, { ...component, content: { ...component.content, [key]: value } });
  };

  switch (component.type) {
    case 'heading':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Heading className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-medium text-slate-400">Heading</span>
          </div>
          <Input value={component.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Heading text..." className="bg-slate-600 border-slate-500 text-white" />
          <div className="flex gap-2">
            <Select value={component.content.level || 'h2'} onValueChange={(val) => updateContent('level', val)}>
              <SelectTrigger className="w-20 bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="h1">H1</SelectItem>
                <SelectItem value="h2">H2</SelectItem>
                <SelectItem value="h3">H3</SelectItem>
              </SelectContent>
            </Select>
            <Select value={component.content.align || 'left'} onValueChange={(val) => updateContent('align', val)}>
              <SelectTrigger className="w-24 bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="left">Left</SelectItem>
                <SelectItem value="center">Center</SelectItem>
                <SelectItem value="right">Right</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    case 'text':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Type className="w-4 h-4 text-green-400" />
            <span className="text-xs font-medium text-slate-400">Text Block</span>
          </div>
          <Textarea value={component.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Text content..." className="bg-slate-600 border-slate-500 text-white min-h-[80px]" />
        </div>
      );
    case 'image':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Image className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-medium text-slate-400">Image</span>
          </div>
          <Input value={component.content.src || ''} onChange={(e) => updateContent('src', e.target.value)} placeholder="Image URL..." className="bg-slate-600 border-slate-500 text-white" />
          <Input value={component.content.alt || ''} onChange={(e) => updateContent('alt', e.target.value)} placeholder="Alt text..." className="bg-slate-600 border-slate-500 text-white" />
        </div>
      );
    case 'button':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Square className="w-4 h-4 text-orange-400" />
            <span className="text-xs font-medium text-slate-400">Button</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={component.content.text || ''} onChange={(e) => updateContent('text', e.target.value)} placeholder="Text..." className="bg-slate-600 border-slate-500 text-white" />
            <Input value={component.content.url || ''} onChange={(e) => updateContent('url', e.target.value)} placeholder="URL..." className="bg-slate-600 border-slate-500 text-white" />
          </div>
        </div>
      );
    case 'divider':
      return (
        <div className="flex items-center gap-2">
          <Minus className="w-4 h-4 text-slate-400" />
          <span className="text-xs font-medium text-slate-400">Divider</span>
          <div className="flex-1 border-t border-slate-500"></div>
        </div>
      );
    case 'spacer':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <AlignVerticalSpaceAround className="w-4 h-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-400">Spacer</span>
          </div>
          <Select value={component.content.size || 'md'} onValueChange={(val) => updateContent('size', val)}>
            <SelectTrigger className="w-32 bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="xs">XS (8px)</SelectItem>
              <SelectItem value="sm">SM (16px)</SelectItem>
              <SelectItem value="md">MD (32px)</SelectItem>
              <SelectItem value="lg">LG (64px)</SelectItem>
              <SelectItem value="xl">XL (96px)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    case 'section':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <PanelTop className="w-4 h-4 text-indigo-400" />
            <span className="text-xs font-medium text-slate-400">Section</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={component.content.bgType || 'none'} onValueChange={(val) => updateContent('bgType', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white"><SelectValue placeholder="Background" /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="light">Light</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="primary">Primary</SelectItem>
              </SelectContent>
            </Select>
            <Select value={component.content.padding || 'md'} onValueChange={(val) => updateContent('padding', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white"><SelectValue placeholder="Padding" /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    case 'container':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Box className="w-4 h-4 text-cyan-400" />
            <span className="text-xs font-medium text-slate-400">Container</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={component.content.maxWidth || 'lg'} onValueChange={(val) => updateContent('maxWidth', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
                <SelectItem value="xl">XL</SelectItem>
              </SelectContent>
            </Select>
            <Select value={component.content.padding || 'md'} onValueChange={(val) => updateContent('padding', val)}>
              <SelectTrigger className="bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="none">None</SelectItem>
                <SelectItem value="sm">Small</SelectItem>
                <SelectItem value="md">Medium</SelectItem>
                <SelectItem value="lg">Large</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    case 'columns':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <LayoutGrid className="w-4 h-4 text-pink-400" />
            <span className="text-xs font-medium text-slate-400">Columns</span>
          </div>
          <Select value={component.content.layout || '2-equal'} onValueChange={(val) => updateContent('layout', val)}>
            <SelectTrigger className="bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="2-equal">2 Equal</SelectItem>
              <SelectItem value="3-equal">3 Equal</SelectItem>
              <SelectItem value="4-equal">4 Equal</SelectItem>
              <SelectItem value="1-2">1/3 + 2/3</SelectItem>
              <SelectItem value="2-1">2/3 + 1/3</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    case 'html':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Code className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-medium text-slate-400">HTML</span>
          </div>
          <Textarea value={component.content.html || ''} onChange={(e) => updateContent('html', e.target.value)} placeholder="<div>HTML...</div>" className="bg-slate-600 border-slate-500 text-white font-mono text-sm" />
        </div>
      );
    case 'tool':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <Target className="w-4 h-4 text-orange-400" />
            <span className="text-xs font-medium text-slate-400">Tool Plugin</span>
          </div>
          <Select value={component.content.tool || 'quick-50kb'} onValueChange={(val) => updateContent('tool', val)}>
            <SelectTrigger className="bg-slate-600 border-slate-500 text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-700 border-slate-600">
              <SelectItem value="quick-50kb">Quick 50KB Compressor</SelectItem>
              <SelectItem value="full-compressor">Full Image Compressor</SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    default:
      return null;
  }
}

export default function PostEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isNew = id === 'new';

  const [post, setPost] = useState({
    title: '',
    slug: '',
    excerpt: '',
    featured_image: '',
    category: '',
    components: [],
    seo: { meta_title: '', meta_description: '', meta_keywords: '', og_image: '', no_index: false, no_follow: false },
    sidebar: { mode: 'global', position: null, width: null, widgets: [] },
    status: 'draft'
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  useEffect(() => {
    if (!isNew) fetchPost();
  }, [id]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API_URL}/api/posts/${id}`);
      if (response.ok) setPost(await response.json());
      else navigate('/admin/posts');
    } catch (error) {
      toast.error('Failed to load post');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!post.title || !post.slug) {
      toast.error('Title and slug are required');
      return;
    }
    setSaving(true);
    try {
      const url = isNew ? `${API_URL}/api/posts` : `${API_URL}/api/posts/${id}`;
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(post)
      });
      if (response.ok) {
        const savedPost = await response.json();
        toast.success(isNew ? 'Post created!' : 'Post saved!');
        if (isNew) navigate(`/admin/posts/${savedPost.id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save');
      }
    } catch (error) {
      toast.error('Error saving post');
    } finally {
      setSaving(false);
    }
  };

  const addComponent = (type) => {
    setPost(prev => ({ 
      ...prev, 
      components: [...prev.components, { id: `comp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, type, content: {}, style: {} }] 
    }));
    toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} added`);
  };

  const updateComponent = (compId, updatedComp) => {
    setPost(prev => ({ ...prev, components: prev.components.map(c => c.id === compId ? updatedComp : c) }));
  };

  const deleteComponent = (compId) => {
    setPost(prev => ({ ...prev, components: prev.components.filter(c => c.id !== compId) }));
    toast.success('Component deleted');
  };

  const duplicateComponent = (component) => {
    const clonedComponent = JSON.parse(JSON.stringify(component));
    clonedComponent.id = `${component.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    if (clonedComponent.content?.columns) {
      clonedComponent.content.columns = clonedComponent.content.columns.map(col => ({
        ...col,
        id: `col_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        components: (col.components || []).map(nestedComp => ({
          ...nestedComp,
          id: `nested_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }))
      }));
    }
    
    if (clonedComponent.content?.children) {
      clonedComponent.content.children = clonedComponent.content.children.map(child => ({
        ...child,
        id: `nested_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }));
    }
    
    setPost(prev => {
      const index = prev.components.findIndex(c => c.id === component.id);
      const newComponents = [...prev.components];
      newComponents.splice(index + 1, 0, clonedComponent);
      return { ...prev, components: newComponents };
    });
    
    toast.success('Component duplicated');
  };

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      setPost(prev => {
        const oldIndex = prev.components.findIndex(c => c.id === active.id);
        const newIndex = prev.components.findIndex(c => c.id === over.id);
        return { ...prev, components: arrayMove(prev.components, oldIndex, newIndex) };
      });
    }
  };

  if (loading) return <div className="h-screen flex items-center justify-center bg-slate-900"><div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div></div>;

  const contentComponents = COMPONENT_TYPES.filter(c => c.category === 'content');
  const layoutComponents = COMPONENT_TYPES.filter(c => c.category === 'layout');

  return (
    <div className="h-screen flex flex-col bg-slate-900">
      <Toaster position="top-center" theme="dark" />
      
      <header className="bg-slate-800 border-b border-slate-700 p-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/posts')} className="text-slate-400 hover:text-white p-2 hover:bg-slate-700 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Input value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} placeholder="Post title..." className="bg-slate-700 border-slate-600 text-white text-lg font-semibold w-64" />
          </div>
          <div className="flex items-center gap-2">
            <Select value={post.status} onValueChange={(val) => setPost({ ...post, status: val })}>
              <SelectTrigger className="w-32 bg-slate-700 border-slate-600 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="published">Published</SelectItem>
              </SelectContent>
            </Select>
            {post.status === 'published' && post.slug && (
              <a href={`/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded">
                <Eye className="w-5 h-5" />
              </a>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        <aside className="w-72 bg-slate-800 border-r border-slate-700 overflow-y-auto flex-shrink-0">
          <Tabs defaultValue="components" className="h-full flex flex-col">
            <TabsList className="w-full bg-slate-700 rounded-none border-b border-slate-600 flex-shrink-0">
              <TabsTrigger value="components" className="flex-1 data-[state=active]:bg-slate-600">Add</TabsTrigger>
              <TabsTrigger value="details" className="flex-1 data-[state=active]:bg-slate-600">Details</TabsTrigger>
              <TabsTrigger value="seo" className="flex-1 data-[state=active]:bg-slate-600">SEO</TabsTrigger>
              <TabsTrigger value="sidebar" className="flex-1 data-[state=active]:bg-slate-600">
                <PanelLeft className="w-3 h-3" />
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="components" className="flex-1 overflow-y-auto p-4 space-y-4 m-0">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Content</p>
                <div className="space-y-1">
                  {contentComponents.map((comp) => (
                    <button key={comp.type} onClick={() => addComponent(comp.type)} className="w-full flex items-center gap-3 p-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-left transition-all hover:translate-x-1">
                      <comp.icon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span className="text-sm text-white">{comp.label}</span>
                      <Plus className="w-3 h-3 text-slate-500 ml-auto" />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Layout & Spacing</p>
                <div className="space-y-1">
                  {layoutComponents.map((comp) => (
                    <button key={comp.type} onClick={() => addComponent(comp.type)} className="w-full flex items-center gap-3 p-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-left transition-all hover:translate-x-1">
                      <comp.icon className="w-4 h-4 text-purple-400 flex-shrink-0" />
                      <span className="text-sm text-white">{comp.label}</span>
                      <Plus className="w-3 h-3 text-slate-500 ml-auto" />
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="details" className="flex-1 overflow-y-auto p-4 space-y-4 m-0">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">URL Slug</label>
                <Input value={post.slug} onChange={(e) => setPost({ ...post, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} placeholder="post-url-slug" className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Excerpt</label>
                <Textarea value={post.excerpt || ''} onChange={(e) => setPost({ ...post, excerpt: e.target.value })} placeholder="Brief summary..." className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Featured Image URL</label>
                <Input value={post.featured_image || ''} onChange={(e) => setPost({ ...post, featured_image: e.target.value })} placeholder="https://..." className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Category</label>
                <Input value={post.category || ''} onChange={(e) => setPost({ ...post, category: e.target.value })} placeholder="Category name" className="bg-slate-700 border-slate-600 text-white" />
              </div>
            </TabsContent>
            
            <TabsContent value="seo" className="flex-1 overflow-y-auto p-4 space-y-4 m-0">
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Meta Title</label>
                <Input value={post.seo.meta_title || ''} onChange={(e) => setPost({ ...post, seo: { ...post.seo, meta_title: e.target.value } })} className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Meta Description</label>
                <Textarea value={post.seo.meta_description || ''} onChange={(e) => setPost({ ...post, seo: { ...post.seo, meta_description: e.target.value } })} className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div>
                <label className="text-xs text-slate-400 mb-1 block">Keywords</label>
                <Input value={post.seo.meta_keywords || ''} onChange={(e) => setPost({ ...post, seo: { ...post.seo, meta_keywords: e.target.value } })} className="bg-slate-700 border-slate-600 text-white" />
              </div>
              <div className="flex items-center justify-between py-2">
                <label className="text-xs text-slate-400">No Index</label>
                <Switch checked={post.seo.no_index || false} onCheckedChange={(val) => setPost({ ...post, seo: { ...post.seo, no_index: val } })} />
              </div>
            </TabsContent>
            
            <TabsContent value="sidebar" className="flex-1 overflow-y-auto p-4 m-0">
              <PageSidebarSettings
                sidebarSettings={post.sidebar || { mode: 'global', position: null, width: null, widgets: [] }}
                onChange={(newSidebar) => setPost({ ...post, sidebar: newSidebar })}
              />
            </TabsContent>
          </Tabs>
        </aside>

        <div className="flex-1 p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Post Content</h2>
              <span className="text-xs text-slate-500">{post.components.length} components</span>
            </div>
            
            {post.components.length === 0 ? (
              <div className="text-center py-16 border-2 border-dashed border-slate-700 rounded-xl bg-slate-800/50">
                <Plus className="w-12 h-12 mx-auto mb-4 text-slate-600" />
                <p className="text-slate-400 mb-2">No components yet</p>
                <p className="text-slate-500 text-sm">Click components in the sidebar to add them</p>
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={post.components.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  {post.components.map((component) => (
                    <SortableComponent key={component.id} component={component} onUpdate={updateComponent} onDelete={deleteComponent} onDuplicate={duplicateComponent} isActive={activeId === component.id} />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
